import React from 'react';
import { motion } from 'framer-motion';
import { Videocam, BrandingWatermark, PhotoCamera, Lightbulb } from '@mui/icons-material';

const ServicesList: React.FC = () => {
  const services = [
    {
      icon: Videocam,
      title: 'Video & Film',
      tagline: 'Capturing Your Vision with Professional Video',
      description: 'Video is a powerful tool to share your story and connect with your audience. We offer high-quality video production services for various needs:',
      items: [
        'Corporate videos',
        'Documentaries',
        'Television shows',
        'Promotional videos'
      ]
    },
    {
      icon: BrandingWatermark,
      title: 'Branding',
      tagline: 'Developing Your Brand Identity',
      description: "Your brand is more than just a logo. It's the essence of your business. We help you create a strong brand identity that resonates with your audience and sets you apart:",
      items: [
        'Brand strategy',
        'Logo design',
        'Brand messaging',
        'Visual identity'
      ]
    },
    {
      icon: PhotoCamera,
      title: 'Digital Content',
      tagline: 'Engaging Your Audience in the Digital World',
      description: "In today's digital age, it's crucial to have a strong online presence. We help you create engaging digital content that drives results:",
      items: [
        'Social Media Content',
        'Photography',
        'Graphic Design',
        'Podcast Production'
      ]
    },
    {
      icon: Lightbulb,
      title: 'Creativity & Communication',
      tagline: 'Developing Innovative Concepts',
      description: 'At the heart of every production is the creative concept. Our talented team understands your brief to find the most effective way to communicate your story from start to finish.'
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h1 
            className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6" 
            style={{ 
              fontFamily: "'Montserrat', sans-serif",
              color: '#EF3F23',
              letterSpacing: '-0.05em'
            }}
          >
            Our Services
          </h1>
        </motion.div>

        <div className="space-y-32 md:space-y-40">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-16 items-center`}
              >
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center gap-6 mb-8">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center border-2 border-[#EF3F23]" style={{ backgroundColor: '#EF3F2315' }}>
                      <IconComponent style={{ color: '#EF3F23', fontSize: '40px' }} />
                    </div>
                    <h2 
                      className="text-4xl md:text-5xl lg:text-6xl font-bold"
                      style={{ 
                        fontFamily: "'Montserrat', sans-serif",
                        color: '#EF3F23',
                        letterSpacing: '-0.05em'
                      }}
                    >
                      {service.title}
                    </h2>
                  </div>
                  <h3 
                    className="text-xl md:text-2xl lg:text-3xl font-semibold text-white mb-6"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {service.tagline}
                  </h3>
                  <p 
                    className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed mb-8"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {service.description}
                  </p>
                  {service.items && (
                    <ul className="space-y-4">
                      {service.items.map((item, idx) => (
                        <li 
                          key={idx} 
                          className="flex items-center gap-3 text-gray-300"
                          style={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem' }}
                        >
                          <span className="w-3 h-3 rounded-full bg-[#EF3F23] flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="w-full lg:w-1/2">
                  {index === 0 ? (
                    <div className="rounded-2xl overflow-hidden border border-gray-700 h-[336px] md:h-[400px] lg:h-[464px]">
                      <iframe
                        width="100%"
                        height="100%"
                        src="https://www.youtube.com/embed/PdMqcKloKxo"
                        title="Video & Film Service"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      ></iframe>
                    </div>
                  ) : index === 1 ? (
                    <div className="rounded-2xl overflow-hidden border border-gray-700 h-[336px] md:h-[400px] lg:h-[464px]">
                      <img
                        src="/pics/Branding.jpg"
                        alt="Branding Service"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : index === 2 ? (
                    <div className="rounded-2xl overflow-hidden border border-gray-700 h-[336px] md:h-[400px] lg:h-[464px]">
                      <img
                        src="/pics/Digital Content.jpg"
                        alt="Digital Content Service"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="rounded-2xl overflow-hidden border border-gray-700 h-[336px] md:h-[400px] lg:h-[464px]">
                      <img
                        src="/pics/Creativity & Communication.JPG"
                        alt="Creativity & Communication Service"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesList;
